import GuideArticle from '../components/GuideArticle'
import type { RelatedCalc, HowToStep } from '../components/GuideArticle'
import type { QA } from '../components/Faq'
import { useLocale, localizedHref } from '../i18n/locale'

interface Content {
  title: string
  h1: string
  description: string
  category: string
  faqs: QA[]
  related: RelatedCalc[]
  // Step-by-step data for HowTo schema. Mirrors the visible phase sections in
  // the body but as plain text so Google's rich result can render the step cards.
  steps: HowToStep[]
}

const STEPS_EN: HowToStep[] = [
  { name: 'Plan and design', text: 'Decide whether the layout stays the same or fixtures move, choose tile, vanity, toilet, fixtures and lighting early, set a 10-20% contingency budget, and check local permit requirements for any plumbing, electrical or structural changes.' },
  { name: 'Demolition', text: 'Shut off water and power to the room, remove fixtures, vanity, flooring and tile down to the studs and subfloor where needed, then inspect for water damage, rot or mould and address it before rebuilding.' },
  { name: 'Rough-in plumbing and electrical', text: 'Run or relocate supply lines and drains for the new layout, add or move wiring for lighting, GFCI outlets, heated floors and the exhaust fan, and book inspections before walls close up.' },
  { name: 'Walls, waterproofing and backer board', text: 'Insulate exterior walls, hang moisture-resistant drywall on non-wet areas, install cement backer board in wet areas, then apply a waterproofing membrane to the shower or tub area and let it cure fully.' },
  { name: 'Tile', text: 'Plan layout lines from the centre so cut tiles land symmetrically at the edges, set floor and wall tile in thinset, let it cure, then grout and seal.' },
  { name: 'Paint', text: 'Use a bathroom-rated mildew-resistant paint for humidity, calculating paint and primer quantities with the Paint Calculator.' },
  { name: 'Fixtures and finishing', text: 'Install the vanity, sink, faucet, toilet and shower or tub hardware, hang lighting, mirror, towel bars and accessories, then caulk all wet joints, test for leaks and run the exhaust fan to confirm airflow.' },
]

const STEPS_FR: HowToStep[] = [
  { name: 'Planifier et concevoir', text: 'Décidez si la disposition reste la même ou si des appareils sont déplacés, choisissez tôt votre carrelage, meuble-lavabo, toilette, appareils et éclairage, prévoyez un budget avec une réserve de 10 à 20 %, et vérifiez les exigences de permis locales.' },
  { name: 'Démolition', text: 'Coupez l\'eau et le courant de la pièce, retirez les appareils, le meuble-lavabo, le revêtement de sol et le carrelage jusqu\'aux montants et au sous-plancher au besoin, puis inspectez pour déceler les dégâts d\'eau, la pourriture ou la moisissure avant de reconstruire.' },
  { name: 'Installation brute de la plomberie et de l\'électricité', text: 'Posez ou relocalisez les conduites d\'alimentation et les drains, ajoutez ou déplacez le câblage pour l\'éclairage, les prises DDFT, le plancher chauffant et le ventilateur d\'extraction, et planifiez les inspections avant de fermer les murs.' },
  { name: 'Murs, étanchéité et panneau d\'appui', text: 'Isolez les murs extérieurs, posez du gypse résistant à l\'humidité sur les zones non mouillées, installez un panneau d\'appui en ciment dans les zones mouillées, puis appliquez une membrane d\'étanchéité et laissez-la durcir complètement.' },
  { name: 'Carrelage', text: 'Tracez vos lignes de pose à partir du centre pour que les tuiles coupées tombent symétriquement, posez le carrelage de plancher et de mur dans le mortier-colle, laissez durcir, puis appliquez le coulis et scellez.' },
  { name: 'Peinture', text: 'Utilisez une peinture adaptée aux salles de bain (résistante à la moisissure) pour l\'humidité, en calculant la peinture et l\'apprêt avec le Calculateur de peinture.' },
  { name: 'Appareils et finition', text: 'Installez le meuble-lavabo, le lavabo, le robinet, la toilette et la quincaillerie de douche ou de baignoire, posez l\'éclairage, le miroir, les barres à serviettes et les accessoires, puis appliquez du scellant à tous les joints mouillés et faites tourner le ventilateur d\'extraction.' },
]

const EN: Content = {
  title: 'Bathroom Renovation Checklist: Step-by-Step Order of Work | RenoSheets',
  h1: 'Bathroom Renovation Checklist',
  description:
    'A complete, ordered checklist for a bathroom remodel — planning, demo, rough-in, waterproofing, tile, fixtures and finishing — so nothing gets done out of sequence.',
  category: 'Planning',
  related: [
    { label: 'Tile Calculator', href: '/calculators/tile/', blurb: 'Floor and wall tile plus thinset, grout, backer board and spacers.' },
    { label: 'Paint Calculator', href: '/calculators/paint/', blurb: 'Bathroom-friendly paint and primer with all the supplies.' },
    { label: 'Flooring Calculator', href: '/calculators/flooring/', blurb: 'Vinyl or laminate if you are not tiling the floor.' },
    { label: 'Drywall Calculator', href: '/calculators/drywall/', blurb: 'Moisture-resistant board for walls and ceilings.' },
  ],
  steps: STEPS_EN,
  faqs: [
    {
      q: 'What is the correct order of work for a bathroom renovation?',
      a: 'Work from the structure outward: demolition, then plumbing and electrical rough-in, then insulation and moisture-resistant drywall/backer board, then waterproofing, then floor and wall tile, then paint, then fixtures (toilet, vanity, sink, faucet), and finally trim and accessories. Roughing in plumbing and electrical before you close up the walls is the step DIYers most often get out of order.',
    },
    {
      q: 'How long does a bathroom renovation take?',
      a: 'A straightforward DIY bathroom remodel commonly runs two to four weeks of working time, largely because of drying and curing: waterproofing, thinset, and grout each need time to cure before the next step. Booking trades (plumber, electrician) and material lead times can stretch the calendar further, so build slack into your schedule.',
    },
    {
      q: 'Do I need a permit to renovate a bathroom?',
      a: 'A cosmetic refresh (paint, a new vanity, swapping a faucet) usually does not need a permit, but moving plumbing or electrical, altering walls, or changing the layout typically does. Permit rules vary by municipality, so check with your local building department before starting work that changes plumbing, wiring, or structure.',
    },
    {
      q: 'How much tile do I need for a bathroom?',
      a: 'Measure each surface you are tiling (floor, and each wall or shower area) in square feet, then add a waste allowance — about 10% for a straight layout in a square room, and 15–20% for diagonal layouts or lots of cuts. The Tile Calculator totals your areas and also gives the thinset, grout, and backer board to go with the tile.',
    },
    {
      q: 'What should I budget for first in a bathroom remodel?',
      a: 'Prioritise the things hidden behind the walls — plumbing, electrical, and proper waterproofing — because fixing those later means tearing out finished surfaces. Spend on quality waterproofing and a reliable exhaust fan before splurging on visible upgrades.',
    },
  ],
}

const FR: Content = {
  title: 'Liste de vérification pour rénover une salle de bain : ordre des travaux | RenoSheets',
  h1: 'Liste de vérification pour rénover une salle de bain',
  description:
    'Une liste de vérification complète et ordonnée pour rénover une salle de bain — planification, démolition, installation brute, étanchéité, carrelage, appareils et finition — pour que rien ne se fasse dans le désordre.',
  category: 'Planification',
  related: [
    { label: 'Calculateur de carrelage', href: '/calculators/tile/', blurb: 'Carrelage de plancher et de mur, plus mortier-colle, coulis, panneau d’appui et croisillons.' },
    { label: 'Calculateur de peinture', href: '/calculators/paint/', blurb: 'Peinture et apprêt adaptés à la salle de bain, avec toutes les fournitures.' },
    { label: 'Calculateur de plancher', href: '/calculators/flooring/', blurb: 'Vinyle ou stratifié si vous ne carrelez pas le plancher.' },
    { label: 'Calculateur de gypse', href: '/calculators/drywall/', blurb: 'Panneau résistant à l’humidité pour les murs et les plafonds.' },
  ],
  steps: STEPS_FR,
  faqs: [
    {
      q: 'Quel est le bon ordre des travaux pour rénover une salle de bain?',
      a: 'Travaillez de la structure vers l’extérieur : démolition, puis installation brute de la plomberie et de l’électricité, puis isolation et gypse résistant à l’humidité ou panneau d’appui, puis étanchéité, puis carrelage du plancher et des murs, puis peinture, puis appareils (toilette, meuble-lavabo, lavabo, robinet), et enfin moulures et accessoires. Faire l’installation brute de la plomberie et de l’électricité avant de fermer les murs est l’étape que les bricoleurs intervertissent le plus souvent.',
    },
    {
      q: 'Combien de temps prend la rénovation d’une salle de bain?',
      a: 'Une rénovation de salle de bain simple, faite soi-même, prend couramment deux à quatre semaines de travail, en grande partie à cause du séchage et de la cure : l’étanchéité, le mortier-colle et le coulis ont chacun besoin de temps pour durcir avant l’étape suivante. Réserver les corps de métier (plombier, électricien) et les délais de livraison des matériaux peuvent allonger davantage le calendrier; prévoyez donc du jeu dans votre échéancier.',
    },
    {
      q: 'Faut-il un permis pour rénover une salle de bain?',
      a: 'Un rafraîchissement cosmétique (peinture, nouveau meuble-lavabo, remplacement d’un robinet) ne nécessite généralement pas de permis, mais déplacer la plomberie ou l’électricité, modifier les murs ou changer la disposition en exige habituellement un. Les règles de permis varient d’une municipalité à l’autre; vérifiez donc auprès de votre service d’urbanisme local avant d’entreprendre des travaux qui modifient la plomberie, le câblage ou la structure.',
    },
    {
      q: 'Combien de carrelage me faut-il pour une salle de bain?',
      a: 'Mesurez chaque surface à carreler (le plancher, et chaque mur ou zone de douche) en pieds carrés, puis ajoutez une marge de perte — environ 10 % pour une pose droite dans une pièce carrée, et 15 à 20 % pour les poses en diagonale ou avec beaucoup de coupes. Le Calculateur de carrelage additionne vos surfaces et donne aussi le mortier-colle, le coulis et le panneau d’appui qui accompagnent le carrelage.',
    },
    {
      q: 'Sur quoi faut-il investir en premier dans une rénovation de salle de bain?',
      a: 'Priorisez ce qui se cache derrière les murs — la plomberie, l’électricité et une bonne étanchéité — parce que les corriger plus tard signifie arracher les surfaces finies. Investissez dans une étanchéité de qualité et un ventilateur d’extraction fiable avant de vous gâter avec des améliorations visibles.',
    },
  ],
}

export default function GuideBathroomChecklist() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <GuideArticle
      title={c.title}
      h1={c.h1}
      description={c.description}
      path="/guides/bathroom-renovation-checklist/"
      updated="2026-06-04"
      category={c.category}
      faqs={c.faqs}
      related={c.related}
      howToSteps={c.steps}
    >
      {locale === 'fr' ? (
        <>
          <p>
            Une salle de bain est la pièce la plus exigeante de la maison en matière de plomberie et d’étanchéité, et c’est
            exactement pourquoi l’ordre des opérations compte autant. Suivez la bonne séquence et le projet avance tout seul;
            faites-la dans le désordre et vous arracherez du carrelage neuf pour réparer un tuyau. Utilisez cette liste pour
            planifier, acheter et planifier une réfection complète de A à Z.
          </p>

          <h2>1. Planifier et concevoir</h2>
          <ul>
            <li>Décidez si la disposition reste la même ou si des appareils sont déplacés (déplacer la plomberie ajoute des coûts et exige habituellement un permis).</li>
            <li>Choisissez tôt votre carrelage, votre meuble-lavabo, votre toilette, vos appareils et votre éclairage — les délais de livraison peuvent atteindre plusieurs semaines.</li>
            <li>Établissez un budget avec une réserve de 10 à 20 % pour les surprises cachées derrière les vieux murs.</li>
            <li>Vérifiez les exigences de permis locales pour tout changement de plomberie, d’électricité ou de structure.</li>
          </ul>

          <h2>2. Démolition</h2>
          <ul>
            <li>Coupez l’eau et le courant de la pièce avant de toucher à quoi que ce soit.</li>
            <li>Retirez les appareils, le meuble-lavabo, le revêtement de sol et le carrelage jusqu’aux montants et au sous-plancher au besoin.</li>
            <li>Inspectez pour déceler les dégâts d’eau, la pourriture ou la moisissure, et corrigez-les avant de reconstruire.</li>
          </ul>

          <h2>3. Installation brute (plomberie et électricité)</h2>
          <ul>
            <li>Posez ou relocalisez les conduites d’alimentation et les drains selon la nouvelle disposition.</li>
            <li>Ajoutez ou déplacez le câblage pour l’éclairage, les prises (DDFT), le plancher chauffant et le ventilateur d’extraction.</li>
            <li>C’est le moment des inspections si votre permis l’exige — avant de fermer les murs.</li>
          </ul>

          <h2>4. Murs, étanchéité et panneau d’appui</h2>
          <ul>
            <li>Isolez les murs extérieurs, puis posez du gypse résistant à l’humidité sur les zones non mouillées. Estimez les feuilles et le composé avec le <a href={L('/calculators/drywall/')}>Calculateur de gypse</a>.</li>
            <li>Installez un panneau d’appui en ciment dans les zones mouillées (douche, contour de baignoire).</li>
            <li>Appliquez une membrane d’étanchéité à la zone de douche ou de baignoire et laissez-la durcir complètement — c’est l’étape la plus importante pour une salle de bain durable.</li>
          </ul>

          <h2>5. Carrelage</h2>
          <ul>
            <li>Tracez vos lignes de pose à partir du centre pour que les tuiles coupées tombent symétriquement aux bordures.</li>
            <li>Posez le carrelage de plancher et de mur dans le mortier-colle, laissez durcir, puis appliquez le coulis et scellez.</li>
            <li>Obtenez le nombre exact de tuiles, plus le mortier-colle, le coulis, les croisillons et le panneau d’appui, avec le <a href={L('/calculators/tile/')}>Calculateur de carrelage</a>.</li>
          </ul>

          <h2>6. Peinture</h2>
          <ul>
            <li>Utilisez une peinture adaptée aux salles de bain (résistante à la moisissure) pour l’humidité. Consultez <a href={L('/guides/how-to-paint-a-room/')}>comment peindre une pièce</a> pour la technique.</li>
            <li>Calculez la peinture et l’apprêt avec le <a href={L('/calculators/paint/')}>Calculateur de peinture</a>.</li>
          </ul>

          <h2>7. Appareils et finition</h2>
          <ul>
            <li>Installez le meuble-lavabo, le lavabo, le robinet, la toilette et la quincaillerie de douche ou de baignoire.</li>
            <li>Posez l’éclairage, le miroir, les barres à serviettes et les accessoires.</li>
            <li>Appliquez du scellant à tous les joints mouillés, vérifiez l’absence de fuites et faites tourner le ventilateur d’extraction pour confirmer le débit d’air.</li>
          </ul>

          <h2>Restez organisé</h2>
          <p>
            Une rénovation de salle de bain comporte beaucoup d’éléments mobiles et de dépendances. Suivez chaque phase comme
            une carte sur le <a href={L('/kanban-board/')}>tableau Kanban</a> gratuit — il existe même un modèle « Rénovation de
            salle de bain » prêt à l’emploi qui préremplit ces tâches pour que vous les fassiez passer de Planification à
            Terminé au fil du chantier.
          </p>
        </>
      ) : (
        <>
          <p>
            A bathroom is the most plumbing- and waterproofing-intensive room in the house, which is exactly why order of
            operations matters so much. Get the sequence right and the project flows; get it wrong and you're tearing out
            fresh tile to fix a pipe. Use this checklist to plan, buy, and schedule a full gut-and-replace.
          </p>

          <h2>1. Plan and design</h2>
          <ul>
            <li>Decide whether the layout stays the same or fixtures move (moving plumbing adds cost and usually a permit).</li>
            <li>Choose your tile, vanity, toilet, fixtures, and lighting early — lead times can be weeks.</li>
            <li>Set a budget with a 10–20% contingency for the surprises hidden behind old walls.</li>
            <li>Check local permit requirements for any plumbing, electrical, or structural changes.</li>
          </ul>

          <h2>2. Demolition</h2>
          <ul>
            <li>Shut off water and power to the room before you touch anything.</li>
            <li>Remove fixtures, vanity, flooring, and tile down to the studs and subfloor where needed.</li>
            <li>Inspect for water damage, rot, or mould and address it before rebuilding.</li>
          </ul>

          <h2>3. Rough-in (plumbing &amp; electrical)</h2>
          <ul>
            <li>Run or relocate supply lines and drains for the new layout.</li>
            <li>Add or move wiring for lighting, outlets (GFCI), heated floors, and the exhaust fan.</li>
            <li>This is the time for inspections if your permit requires them — before walls close up.</li>
          </ul>

          <h2>4. Walls, waterproofing &amp; backer board</h2>
          <ul>
            <li>Insulate exterior walls, then hang moisture-resistant drywall on non-wet areas. Estimate sheets and compound with the <a href={L('/calculators/drywall/')}>Drywall Calculator</a>.</li>
            <li>Install cement backer board in wet areas (shower, tub surround).</li>
            <li>Apply a waterproofing membrane to the shower/tub area and let it cure fully — this is the single most important step for a lasting bathroom.</li>
          </ul>

          <h2>5. Tile</h2>
          <ul>
            <li>Plan your layout lines from the centre so cut tiles land symmetrically at the edges.</li>
            <li>Set floor and wall tile in thinset, let it cure, then grout and seal.</li>
            <li>Get exact tile counts plus thinset, grout, spacers, and backer board from the <a href={L('/calculators/tile/')}>Tile Calculator</a>.</li>
          </ul>

          <h2>6. Paint</h2>
          <ul>
            <li>Use a bathroom-rated (mildew-resistant) paint for humidity. See <a href={L('/guides/how-to-paint-a-room/')}>how to paint a room</a> for technique.</li>
            <li>Calculate paint and primer with the <a href={L('/calculators/paint/')}>Paint Calculator</a>.</li>
          </ul>

          <h2>7. Fixtures &amp; finishing</h2>
          <ul>
            <li>Install the vanity, sink, faucet, toilet, and shower/tub hardware.</li>
            <li>Hang lighting, mirror, towel bars, and accessories.</li>
            <li>Caulk all wet joints, test for leaks, and run the exhaust fan to confirm airflow.</li>
          </ul>

          <h2>Keep it organized</h2>
          <p>
            A bathroom remodel has a lot of moving parts and dependencies. Track each phase as a card on the free{' '}
            <a href={L('/kanban-board/')}>kanban board</a> — there's even a ready-made "Bathroom Renovation" template that pre-fills
            these tasks so you can move them from Planning to Done as the job progresses.
          </p>
        </>
      )}
    </GuideArticle>
  )
}
